import equal from 'deep-equal';
import type { FormikActions, FormikProps } from 'formik';
import { Formik } from 'formik';
import { Log } from 'ng2-logger';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Select from 'react-select';
import type { ValueType } from 'react-select';
import Button from '../../components/button';
import Paginate from '../../components/table/paginate';
import SortedColumn from '../../components/table/sorted-column';
import APlanHeader from '../../components/templates/aplan-header';
import Spinner from '../../components/templates/spinner';
import Translator from '../../services/translate-factory';
import * as Actions from '../../store/actions/general';
import * as Constants from '../../store/constants/all';
import {
  CoursePeriodSearchInitialValues,
  TermTypes,
} from '../../store/constants/course-period-const';
import { routes as Routes } from '../../store/constants/routes';
import type * as Types from '../../store/types';
import * as GT from '../../tools/general-tools';
import MainLayout from '../layouts/main-layout';
import CalendarModal from './calendar-modal';
import CopyPeriodForm from './copy-period-form';
import CoursePeriodForm from './course-period-form';

const T = Translator.create();
const L = Log.create('CoursePeriodListPage');

class CoursePeriodTable extends Component<
  Types.ICoursePeriodPageProps,
  Types.ICoursePeriodPageState
> {
  state: Types.ICoursePeriodPageState = {
    filters: CoursePeriodSearchInitialValues,
    filterIsOpen: false,
    coursePeriodFormIsOpen: false,
    calendarFormIsOpen: false,
    coursePeriodId: undefined,
    copyFormIsOpen: false,
    copyFormTermId: undefined,
  };

  langChanged = () => {
    try {
      this.forceUpdate();
    } catch (e) {
      L.error(e as string);
    }
  };

  componentDidMount() {
    T.removeListener(Constants.gen.CORE_CHANGE_LANGUAGE, this.langChanged);
    T.addListener(Constants.gen.CORE_CHANGE_LANGUAGE, this.langChanged);
    window.scrollTo(0, 0);
    this.searchCoursePeriods();
    this.updateTermListForTermSelector();
  }

  componentWillUnmount() {
    T.removeListener(Constants.gen.CORE_CHANGE_LANGUAGE, this.langChanged);
  }

  updateTermListForTermSelector() {
    // redirect to EXAM_PERIOD_GET_LIST if term_id is not set
    this.props.dispatch(
      Actions.ApiRequest(
        Constants.course_period.COURSE_PERIOD_GET_LIST,
        null,
        'term-selector-spinner',
      ),
    );
  }

  getSelectedPeriodInfo(term_id: number) {
    this.props.dispatch(
      Actions.ApiRequest(
        Constants.exam_period.EXAM_PERIOD_GET_PERIOD_DATES,
        term_id,
        'course-period-modal-tab-spin',
      ),
    );

    const model: Types.IFilterSolutionModel = {
      term_id,
      active_tab: '0',
      page: 1,
      total: -1,
      size: 10,
      solution_id: 0,
    };
    this.props.dispatch(
      Actions.ApiRequest(
        Constants.solution.SOLUTION_GET_BY_TERM,
        model,
        'solution-schedule-spinner',
      ),
    );
  }

  getTermWeekDates(term_id: number) {
    const model: Types.IFilterExamPeriodCalendarModal = {
      term_id,
      page: 1,
      total: -1,
      size: 10,
      status: [1],
    };
    this.props.dispatch(
      Actions.ApiRequest(
        Constants.exam_period.COURSE_PERIOD_GET_TERM_WEEK_DATES,
        model,
        'exam-period-modal-tab-spin',
      ),
    );
  }

  copyFormIsOpen = (item: any) => {
    this.state.copyFormIsOpen = true;
    this.state.copyFormTermId = item.term_id;
    this.setState(this.state);
  };

  copyFormOnClose = (refresh: boolean) => {
    if (this.state.copyFormIsOpen) {
      this.props.dispatch(Actions.Navigation(GT.Route(Routes.COURSE_PERIODS)));
    }
    if (refresh) {
      this.searchCoursePeriods();
    }
    this.state.copyFormIsOpen = false;
  };

  searchCoursePeriods() {
    this.props.dispatch(
      Actions.ApiRequest(
        Constants.course_period.COURSE_PERIOD_LIST_SEARCH,
        this.state.filters,
        'coursePeriod-list-spin',
      ),
    );
  }

  sort = (sortkey: string, order_by: string) => {
    this.state.filters.order_by = `${sortkey}_${order_by}`;
    this.setState(this.state, () => {
      this.searchCoursePeriods();
    });
  };

  coursePeriodFormIsOpen = () => {
    this.props.dispatch(Actions.Navigation(GT.Route(Routes.COURSE_PERIODS, '/create')));
  };

  coursePeriodFormOnClose = (refresh: boolean) => {
    if (this.state.coursePeriodFormIsOpen) {
      this.props.dispatch(Actions.Navigation(GT.Route(Routes.COURSE_PERIODS)));
    }
    if (refresh) {
      this.searchCoursePeriods();
    }
  };

  calendarFormIsOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e && e.currentTarget) {
      const id: string = e.currentTarget.dataset.id || '';
      this.setState(this.state);
      this.props.dispatch(Actions.Navigation(GT.Route(Routes.COURSE_PERIODS, `/calendar/${id}`)));
    }
  };

  calendarFormOnClose = () => {
    this.props.dispatch(Actions.Navigation(GT.Route(Routes.COURSE_PERIODS)));
  };

  onPageChange = (page: number, size?: number) => {
    this.setState(
      (prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          page,
          size: size ? size : 10,
        },
      }),
      this.searchCoursePeriods,
    );
  };

  onSizeChange = (size: number) => {
    this.setState(
      (prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          size,
        },
      }),
      this.searchCoursePeriods,
    );
  };

  onFormReset = () => {
    this.state.filters = Object.assign({}, CoursePeriodSearchInitialValues);
    this.setState(this.state, () => {
      this.searchCoursePeriods();
    });
  };

  onFilterCoursePeriod(
    model: Types.IFilterCoursePeriod,
    FormActions: FormikActions<Types.IFilterCoursePeriod>,
  ) {
    this.setState(
      (prev) => ({
        ...prev,
        filters: {
          ...model,
          page: 1,
        },
        filterIsOpen: true,
        selected_ids: [],
      }),
      () => {
        this.searchCoursePeriods();
        FormActions.setSubmitting(false);
      },
    );
  }

  onCoursePeriodEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e && e.currentTarget) {
      const id: string = e.currentTarget.dataset.id || '';

      this.props.dispatch(Actions.Navigation(GT.Route(Routes.COURSE_PERIODS, `/${id}`)));
    }
  };

  onCoursePeriodDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (e?.currentTarget.dataset?.id) {
      const { id } = e.currentTarget.dataset;
      const { name } = e.currentTarget.dataset;
      const resultCallBack = () => {
        this.updateTermListForTermSelector();
      };
      const onConfirmDelete = () => {
        this.props.dispatch(
          Actions.ApiRequest(
            Constants.course_period.COURSE_PERIOD_DELETE,
            id,
            undefined,
            resultCallBack,
          ),
        );
      };
      this.props.dispatch(
        Actions.ShowModal({
          title: T.t('gen_course_term_will_be_deleted'),
          body: `${T.t('gen_course_term_will_be_deleted_question')} (${name})`,
          name: 'coursePeriod_delete',
          icon: 'error_outline',
          iconColor: 'red',
          confirm: T.t('gen_yes'),
          cancel: T.t('gen_no'),
          onConfirm: onConfirmDelete,
        }),
      );
    }
  };

  static getDerivedStateFromProps(
    props: Types.ICoursePeriodPageProps,
    state: Types.ICoursePeriodPageState,
  ) {
    let hasNewState = false;
    if (props.match?.params.id) {
      const regexObject = props.location.pathname.match(/[^/]+$/);
      hasNewState = true;

      if (props.location?.pathname.includes('calendar')) {
        state.calendarFormIsOpen = true;
        // regexObject = props.location.pathname.match(/([^\/]+$)/); // gets the id of the calendar
        state.coursePeriodId = parseInt(regexObject[0], 10);
      } else if (props.location && regexObject[0] === 'create') {
        state.coursePeriodFormIsOpen = true;
      } else if (props.location && regexObject[0] !== 'create') {
        state.coursePeriodId = props.match.params.id;
        state.coursePeriodFormIsOpen = true;
      }
    } else {
      hasNewState = true;
      state.coursePeriodFormIsOpen = false;
      state.calendarFormIsOpen = false;
      state.coursePeriodId = undefined;
    }

    if (hasNewState) {
      return state;
    } else {
      return null;
    }
  }

  onTermSelect = (term_id: any, term_type: any) => {
    this.props.synchronization && (this.props.synchronization.synctype_campus_clicked = false);
    this.props.synchronization && (this.props.synchronization.synctype_building_clicked = false);
    this.props.synchronization && (this.props.synchronization.synctype_classroom_clicked = false);
    this.props.synchronization && (this.props.synchronization.synctype_faculty_clicked = false);
    this.props.synchronization && (this.props.synchronization.synctype_program_clicked = false);
    this.props.synchronization && (this.props.synchronization.synctype_instructor_clicked = false);
    this.props.synchronization && (this.props.synchronization.synctype_course_clicked = false);
    this.props.synchronization && (this.props.synchronization.synctype_student_clicked = false);

    this.props.dispatch(Actions.SetTermInfo(term_id, term_type));
    this.props.dispatch(
      Actions.ApiRequest(
        Constants.disributor.GET_DISTRIBUTION_IN_PROGRESS_STATUS,
        term_id,
        'ep-student-list',
      ),
    );
    this.props.dispatch(
      Actions.Navigation(GT.Route(term_type == 0 ? Routes.COURSE_PERIODS : Routes.EXAM_PERIODS)),
    );
    this.props.dispatch(
      Actions.Navigation(GT.Route(term_type == 0 ? Routes.COURSE_PERIODS : Routes.EXAM_PERIODS)),
    );
    this.getSelectedPeriodInfo(term_id);
    this.getTermWeekDates(term_id);
  };

  getCalendarModal() {
    if (this.props.location?.pathname.includes('calendar')) {
      return (
        <CalendarModal
          coursePeriodId={this.state.coursePeriodId || this.props.term_id}
          formIsOpen={this.state.calendarFormIsOpen}
          onClose={this.calendarFormOnClose}
        />
      );
    }
    return null;
  }

  getTermSelectFilter(props: FormikProps<Types.IFilterCoursePeriod>) {
    return (
      <div className="col-md-4 form-input">
        <div className="react-select-container">
          <h6>{T.t('gen_term')}</h6>
          <Select
            id="select_term"
            className="react-select"
            isMulti={true}
            filterOption={(option: any, query: any) =>
              option.label
                .toLocaleLowerCase(T.getValidLocaleForComparison())
                .includes(query.toLocaleLowerCase(T.getValidLocaleForComparison()))
            }
            closeMenuOnSelect={false}
            options={TermTypes(T)}
            placeholder={T.t('gen_select_term')}
            value={props.values.terms ? props.values.terms : null}
            onChange={(
              options: ValueType<Types.ISelectOption> | ValueType<Types.ISelectOption[]>,
            ) => {
              const list: Types.ISelectOption[] = options ? (options as Types.ISelectOption[]) : [];
              props.setFieldValue('terms', list);
              props.setFieldValue(
                'term_ids',
                list.map((item) => item.value),
              );
            }}
            noOptionsMessage={(): string => T.t('gen_select_no_term')}
          />
        </div>
      </div>
    );
  }

  getButtonStatus(status: number) {
    return (
      <td scope="row" data-label={T.t('gen_status')}>
        <div className="tags ms-1 me-4">
          <button
            id="button_status"
            className={`tw-p-1 small-tag text-uppercase${status == 1 ? ' co-green' : ' tag-red'}`}
          >
            {GT.GetActiveStatus(status)}
          </button>
        </div>
      </td>
    );
  }

  getCalendarStatus(calendar_status: number) {
    return (
      <td data-label={T.t('gen_course_calendar')} className="text-center">
        {calendar_status == 1 ? (
          <span className="badge co-green" style={{ fontSize: '95%', textAlign: 'center' }}>
            {T.t('gen_badge_created')}
          </span>
        ) : (
          <span className="badge badge-info" style={{ fontSize: '95%', textAlign: 'center' }}>
            {T.t('gen_badge_not_created')}
          </span>
        )}
      </td>
    );
  }

  getEditDeleteButtons(item: Types.ITermItem) {
    return this.props.user && this.props.user.role === 's' ? (
      <td data-label={T.t('gen_actions')} className="table-buttons">
        <div className="table-buttons-wrapper">
          <button
            id="button_edit"
            data-toggle="tooltip"
            data-id={item.term_id}
            onClick={this.onCoursePeriodEdit}
            title={T.t('gen_edit')}
            className="ghost square btn-sm table-button"
          >
            <span className="d-block" data-toggle="modal" data-target="#addNew">
              <i className="material-icons">edit</i>
            </span>
          </button>
          <button
            id="button_delete"
            data-toggle="tooltip"
            data-id={item.term_id}
            data-name={item.name}
            onClick={this.onCoursePeriodDelete}
            title={T.t('gen_delete')}
            className="ghost square btn-sm table-button"
          >
            <span className="d-block" data-toggle="modal" data-target="#coursepleModal">
              <i className="material-icons">delete</i>
            </span>
          </button>
        </div>
      </td>
    ) : null;
  }

  isAdmin() {
    return this.props.user && this.props.user.role === 's';
  }

  render() {
    const calendarModal = this.getCalendarModal();

    let coursePeriodList = this.props.results;
    // ! TODO this is temporary solution for unactive course periods visibility, this should be removed after the filter is implemented to the backend, forced by @ibrahim-gurler
    if (!this.isAdmin()) {
      coursePeriodList = coursePeriodList?.filter((item: Types.ITermItem) => item.status == 1);
    }

    return (
      <MainLayout header={<APlanHeader />}>
        <div
          className="main list-campus"
          style={{ display: this.state.calendarFormIsOpen ? 'none' : 'block' }}
        >
          <div className="container-fluid">
            <div className="row align-items-center mb-sm-4 mb-2">
              <div className="col-md-6 col-sm-4 col-12">
                <h4 className="mb-4 mb-sm-0">{T.t('gen_course_terms')}</h4>
              </div>
              <div className="col-md-6 col-sm-8 col-12 text-right">
                <div className="options align-items-center with-gap right">
                  <button
                    id="button_filter"
                    className="ghost"
                    onClick={() => {
                      this.state.filterIsOpen = !this.state.filterIsOpen;
                      this.setState(this.state);
                    }}
                  >
                    <i className="material-icons">filter_list</i>
                    <span>{T.t('gen_filter')}</span>
                  </button>
                  {this.isAdmin() ? (
                    <button
                      id="button_add_course_term"
                      className="primary"
                      onClick={this.coursePeriodFormIsOpen}
                    >
                      <i className="material-icons">add</i>
                      {T.t('gen_add_course_term')}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
            <div
              className={`white-container mt-4 collapse ${this.state.filterIsOpen ? 'show' : ''}`}
              id="advance-search"
            >
              <div className="advance-search d-block mt-3">
                <Formik
                  initialValues={CoursePeriodSearchInitialValues}
                  enableReinitialize={true}
                  onSubmit={(values, actions) => {
                    this.onFilterCoursePeriod(values, actions);
                  }}
                  onReset={this.onFormReset}
                >
                  {(props: FormikProps<Types.IFilterCoursePeriod>) => {
                    return (
                      <form onSubmit={props.handleSubmit}>
                        <div className="row">
                          <div className="col-12">
                            <h6>{T.t('gen_filter_parameters')}</h6>
                          </div>
                          <div className="col-md-4">
                            <div className="form-input form-group date-picker">
                              <input
                                id="year"
                                name="year"
                                value={props.values.year}
                                onChange={props.handleChange}
                                type="number"
                              />
                              <span className="highlight" />
                              <span className="bar" />
                              <label htmlFor="year">{T.t('gen_year')}</label>
                            </div>
                          </div>
                          {this.getTermSelectFilter(props)}
                          <div className="col-md-4">
                            <div className="form-input form-group date-picker">
                              <input
                                id="name"
                                name="name"
                                value={props.values.name}
                                onChange={props.handleChange}
                                type="text"
                                required={false}
                              />
                              <span className="highlight" />
                              <span className="bar" />
                              <label htmlFor="name">{T.t('gen_term_name')}</label>
                              <i className="material-icons">title</i>
                            </div>
                          </div>
                        </div>
                        <hr />
                        <div className="row mt-3">
                          <div className="col-6 with-gap">
                            <button
                              id="button_arrow_upward"
                              type="button"
                              onClick={() => {
                                this.state.filterIsOpen = false;
                                this.setState(this.state);
                              }}
                              className="ghost"
                            >
                              <i className="material-icons">arrow_upward</i>
                            </button>
                            <button
                              id="button_delete_sweep"
                              type="reset"
                              onClick={props.handleReset}
                              className="danger"
                            >
                              <i className="material-icons">delete_sweep</i>
                            </button>
                          </div>
                          <div className="col-6 text-right">
                            <button
                              id="button_search"
                              type="button"
                              className="secondary xl"
                              onClick={() => props.handleSubmit()}
                              disabled={props.isSubmitting}
                            >
                              <i className="material-icons">search</i> {T.t('gen_search')}
                            </button>
                          </div>
                        </div>
                      </form>
                    );
                  }}
                </Formik>
              </div>
            </div>
            <Spinner name="coursePeriod-list-spin" />
            <div className="white-container mt-4">
              <div className="row">
                <div className="col-12">
                  <table className="aplan-table aplan-table-responsive table table-borderless table-striped table-hover sortable filter-table">
                    <thead>
                      <tr>
                        <SortedColumn
                          datacell="status"
                          title={T.t('gen_status')}
                          sortkey="status"
                          sortedcolumn={this.state.filters.order_by}
                          sort={this.sort}
                          style={{ width: '5%' }}
                        />
                        <SortedColumn
                          datacell="id"
                          title="ID"
                          sortkey="id"
                          sortedcolumn={this.state.filters.order_by}
                          sort={this.sort}
                        />
                        <SortedColumn
                          datacell="year"
                          className="d-none d-lg-table-cell d-xl-table-cell"
                          title={T.t('gen_year')}
                          sortkey="year"
                          sortedcolumn={this.state.filters.order_by}
                          sort={this.sort}
                        />
                        <SortedColumn
                          datacell="academic_term"
                          className="d-none d-lg-table-cell d-xl-table-cell"
                          title={T.t('gen_term')}
                          sortkey="academic_term"
                          sortedcolumn={this.state.filters.order_by}
                          sort={this.sort}
                        />
                        <SortedColumn
                          datacell="name"
                          className="d-none d-lg-table-cell d-xl-table-cell"
                          title={T.t('gen_name')}
                          sortkey="name"
                          sortedcolumn={this.state.filters.order_by}
                          sort={this.sort}
                        />
                        <th scope="col" className="text-center">
                          {T.t('gen_course_calendar')}
                        </th>
                        <th scope="col" className="text-center">
                          {T.t('gen_description')}
                        </th>
                        {this.isAdmin() ? (
                          <>
                            <th scope="col" className="text-center">
                              {T.t('gen_planned_course_count')}
                            </th>
                            <th scope="col" className="text-center">
                              {T.t('gen_distribution_count')}
                            </th>
                            <th scope="col" className="text-center">
                              {T.t('gen_solution_count')}
                            </th>
                          </>
                        ) : null}
                        <th scope="col" className="text-center">
                          {T.t('gen_select_term')}
                        </th>
                        {this.isAdmin() ? (
                          <>
                            <th scope="col" className="text-center">
                              {T.t('gen_copy_term')}
                            </th>
                            <th scope="col" className="text-right">
                              <span className="text-right">{T.t('gen_actions')}</span>
                            </th>
                          </>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {coursePeriodList?.map((item: Types.ITermItem) => {
                        const termObject = TermTypes(T).find(
                          (object) => object.value == item.academic_term,
                        );
                        return (
                          <tr key={`coursePeriod-${item.term_id}`} data-title={item.name}>
                            {this.getButtonStatus(item.status)}

                            <td scope="row" data-label="ID">
                              {item.term_id}
                            </td>
                            <td data-label={T.t('gen_year')}>{item.year}</td>
                            <td data-label={T.t('gen_term')}>{termObject?.label}</td>
                            <td data-label={T.t('gen_name')}>{item.name}</td>
                            {this.getCalendarStatus(item.calendar_status)}
                            <td data-label={T.t('gen_description')} className="text-center">
                              <div className="table-scrollable-td">
                                {item.description.length > 0 ? item.description : '-'}
                              </div>
                            </td>
                            {this.isAdmin() ? (
                              <React.Fragment>
                                <td
                                  data-label={T.t('gen_planned_course_count')}
                                  className="text-center"
                                >
                                  {item.planned_course_count}
                                </td>
                                <td
                                  data-label={T.t('gen_distribution_count')}
                                  className="text-center"
                                >
                                  {item.distribution_count}
                                </td>
                                <td data-label={T.t('gen_solution_count')} className="text-center">
                                  {item.solution_count}
                                </td>
                              </React.Fragment>
                            ) : null}
                            <td data-cell="exam-periods" className="text-center">
                              <Button
                                size="disable"
                                id="button_term_type"
                                className="glyph xs aplan"
                                onClick={() => this.onTermSelect(item.term_id, item.term_type)}
                                icon="external-link"
                                iconPosition="right"
                              >
                                {item.name}
                              </Button>
                            </td>
                            {this.isAdmin() ? (
                              <td className="text-center">
                                <button
                                  id="button_copy_term"
                                  className="tertiary"
                                  onClick={() => this.copyFormIsOpen(item)}
                                >
                                  <i className="material-icons">add</i>
                                  {T.t('gen_copy_term')}
                                </button>
                              </td>
                            ) : null}
                            {this.getEditDeleteButtons(item)}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="row-options justify-content-end">
                    <div
                      className="page-sorting d-flex align-items-center justify-content-center"
                      style={{ marginTop: '5px' }}
                    >
                      {this.props.results && this.props.results.length > 0 && (
                        <Paginate
                          filters={this.props.filters}
                          onPageChange={this.onPageChange}
                          onSizeChange={this.onSizeChange}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {calendarModal}
        {this.state.copyFormIsOpen && (
          <CopyPeriodForm
            coursePeriodId={this.state.copyFormTermId}
            formIsOpen={this.state.copyFormIsOpen}
            onClose={this.copyFormOnClose}
          />
        )}
        <CoursePeriodForm
          coursePeriodId={this.state.coursePeriodId}
          formIsOpen={this.state.coursePeriodFormIsOpen}
          onClose={this.coursePeriodFormOnClose}
        />
      </MainLayout>
    );
  }
}

const mapStateToProps = (
  store: Types.IPersistedState,
  ownProps: Types.ICoursePeriodPageProps,
): Types.ICoursePeriodPageProps => {
  if (!store) {
    return ownProps;
  }

  const newProps: Types.ICoursePeriodPageProps = Object.assign({}, ownProps, {
    results: store.state.course_period_page?.results,
    filters: store.state.course_period_page?.filters,
    term_id: store.state.term_id,
    user: store.state.user,
    synchronization: store.state.synchronization && store.state.synchronization,
  });
  return newProps;
};

const areStatesEqual = (next: Types.IPersistedState, prev: Types.IPersistedState) => {
  const nextResults = next.state.course_period_page?.results
    ? next.state.course_period_page.results
    : [];
  const prevResults = prev.state.course_period_page?.results
    ? prev.state.course_period_page.results
    : [];
  if (nextResults) {
    return !!equal(prevResults, nextResults);
  } else {
    return true;
  }
};

const dispatchProps = (dispatch: any) => ({ dispatch });

const container = connect(mapStateToProps, dispatchProps, null, {
  areStatesEqual,
})(CoursePeriodTable);

export default container;
